import React from 'react';
import { I18n } from 'react-redux-i18n';

import { I18N_BASE } from '../../constants';
import {
  ElementRole,
  elementsByRole,
  EventSnapshot,
  EventSnapshotElement,
  isImageRole,
  ModerationEvent,
} from '../../models';
import SnapshotField from '../../components/SnapshotField/SnapshotField';
import { resolveAboutImagePath, resolveHeroPath } from './imageUrls';
import styles from './review.module.css';

export interface PageSnapshotCardProps {
  event: ModerationEvent;
  snapshot: EventSnapshot;
  testId?: string;
}

// Display order for page-subject snapshots. blobImage is handled by the
// dedicated ImageBlobSnapshotCard, so it's not in this list.
const ROLE_ORDER: readonly ElementRole[] = [
  'pageName',
  'url',
  'bodyText',
  'heroImage',
  'bodyImage',
  'teamName',
  'organizationName',
  'metrics',
];

function roleLabel(role: ElementRole): string {
  return I18n.t(`${I18N_BASE}.detail.snapshot.role.${role}`);
}

function resolveForRole(role: ElementRole, value: string | null): string {
  // heroImage gets the larger hero transform; other body images the standard
  // about transform. Both render in the same tile, but the CDN width hint
  // keeps fetches lean.
  return role === 'heroImage' ? resolveHeroPath(value) : resolveAboutImagePath(value);
}

function ImageElementField({
  role,
  elements,
}: {
  role: ElementRole;
  elements: EventSnapshotElement[];
}): React.ReactElement {
  const anyFlagged = elements.some((e) => e.flagged);
  return (
    <SnapshotField
      label={roleLabel(role)}
      flagged={anyFlagged}
      testId={`snapshot-${role}`}
    >
      <div className={styles.aboutImagesRow}>
        {elements.map((el) => (
          <img
            key={el.guid}
            className={`${styles.imageTile} ${el.flagged ? styles.imageTileFlagged : ''}`}
            src={resolveForRole(role, el.value)}
            alt=""
          />
        ))}
      </div>
    </SnapshotField>
  );
}

export function PageSnapshotCard({
  snapshot,
  testId,
}: PageSnapshotCardProps): React.ReactElement {
  return (
    <section className={styles.snapshotCard} data-testid={testId}>
      {ROLE_ORDER.flatMap((role) => {
        const elements = elementsByRole(snapshot.elements, role);
        if (elements.length === 0) return [];

        if (isImageRole(role)) {
          // Image elements can come back with an empty/null value — skip those
          // so we never render an <img src=""> (broken-image icon). If none of
          // the elements for this role have a value, drop the field entirely.
          const renderable = elements.filter((e) => Boolean(e.value));
          if (renderable.length === 0) return [];
          return [<ImageElementField key={role} role={role} elements={renderable} />];
        }

        // Text roles — one field per element (normally a single element, but
        // the API permits multiples; suffix with the guid so each rendered
        // tile gets a unique data-testid).
        return elements.map((el, idx) => (
          <SnapshotField
            key={el.guid}
            label={roleLabel(role)}
            flagged={el.flagged}
            testId={idx === 0 ? `snapshot-${role}` : `snapshot-${role}-${el.guid}`}
          >
            {el.value || <span className={styles.subtitle}>—</span>}
            {role === 'bodyText' && snapshot.bodyTruncated ? (
              <div className={styles.signalFootnote}>
                {I18n.t(`${I18N_BASE}.detail.snapshot.bodyTruncated`)}
              </div>
            ) : null}
          </SnapshotField>
        ));
      })}
    </section>
  );
}

export default PageSnapshotCard;
